'use client';

import React from 'react';
import { useFormik } from 'formik';
import { stringifyUrl } from 'query-string';
import range from 'ramda/src/range';
import { Typography } from '../../../../../../components/Typography';
import { Autocomplete } from '../../../../../../components/AutocompleteV2';
import { Dropdown } from '../../../../../../components/Dropdown';
import { VetChatHeader } from '../VetChatHeader';
import { schema, FormFields } from './schema';
import { getFormikFieldHelper } from '../../../../../../utils';
import { getDogBreedAssetSuggestions } from '../../../../../../hooks';
import { QueryStringKeys, WagWebAppUrl, petWeightOptions } from '../../../../../../constants';
import { Box } from '../../../../../../wag-react/next-components/box';
import { theme } from '../../../../../../wag-react/themes/base-theme';
import { Grid } from '../../../../../../wag-react/core-components/grid';
import { TextField } from '../../../../../../wag-react/core-components/textfield';
import { Button } from '../../../../../../wag-react/next-components/button';
const getAgeOptions = () => {
  const yearStartDate = new Date();
  yearStartDate.setFullYear(yearStartDate.getFullYear() + 1);
  const yearStart = yearStartDate.getFullYear();
  const yearEnd = new Date(1970).getFullYear();
  return range(yearEnd, yearStart).map(year => ({
    label: String(year),
    value: String(year)
  })).reverse();
};
export const VetChatForm = () => {
  const ageOptions = getAgeOptions();
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    touched,
    values
  } = useFormik({
    validateOnMount: false,
    initialValues: schema.cast(undefined),
    validationSchema: schema,
    onSubmit: () => {
      const url = stringifyUrl({
        url: WagWebAppUrl.WagHealthVetChatCheckout,
        query: {
          [QueryStringKeys.PetName]: values[FormFields.Name],
          /**
           * Following the format: “01-01-{year}”
           */
          [QueryStringKeys.PetBirthday]: new Date(values[FormFields.Age].value, 0, 1).toLocaleDateString(),
          [QueryStringKeys.PetBreed]: values[FormFields.Breed].value,
          [QueryStringKeys.PetWeight]: values[FormFields.Weight].value
        }
      });
      // Opens url in new tab
      window.open(url, '_blank');
    }
  });
  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched
  });
  return <Box data-sentry-element="Box" data-sentry-component="VetChatForm" data-sentry-source-file="index.tsx">
      <VetChatHeader data-sentry-element="VetChatHeader" data-sentry-source-file="index.tsx" />
      <Box backgroundColor={theme.colors.white} minHeight="300px" maxHeight="400px" boxShadow="0 0 9px 0 rgba(0,0,0,0.16)" borderRadius="0 0 8px 8px" py="16px" px="32px" data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Typography fontFamily="muliBold" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          Tell us about your pet
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid gridGap="8px" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            <TextField label="Pet name" name={FormFields.Name} value={values[FormFields.Name]} onChange={handleChange} onBlur={handleBlur} error={formikFieldHelper(FormFields.Name).hasError} helperText={formikFieldHelper(FormFields.Name).errorMessage} fullWidth data-sentry-element="TextField" data-sentry-source-file="index.tsx" />
            <Autocomplete allowCreateNewOptionOnBlur allowEditCurrentOptionOnFocus defaultOptions isCreatable name={FormFields.Breed} label="Breed (type to search)" value={values[FormFields.Breed]} loadOptions={getDogBreedAssetSuggestions} error={formikFieldHelper(FormFields.Breed).hasError} helperText={formikFieldHelper(FormFields.Breed).errorMessage} onChange={value => setFieldValue(FormFields.Breed, value || '')} onBlur={() => {
            // https://github.com/JedWatson/react-select/issues/3737
            // Issue on mobile devices
            setTimeout(() => {
              // custom on-blur
              handleBlur({
                target: {
                  name: FormFields.Breed
                }
              });
            }, 0);
          }} data-sentry-element="Autocomplete" data-sentry-source-file="index.tsx" />
            <Grid gridTemplateColumns={['1fr', '1fr 1fr']} gridGap="8px" data-sentry-element="Grid" data-sentry-source-file="index.tsx">
              <Dropdown name={FormFields.Age} label="Age" value={values[FormFields.Age]} options={ageOptions} error={formikFieldHelper(FormFields.Age).hasError} helperText={formikFieldHelper(FormFields.Age).errorMessage} onChange={value => setFieldValue(FormFields.Age, value || '')} onBlur={() => {
              // https://github.com/JedWatson/react-select/issues/3737
              // Issue on mobile devices
              setTimeout(() => {
                // custom on-blur
                handleBlur({
                  target: {
                    name: FormFields.Age
                  }
                });
              }, 0);
            }} data-sentry-element="Dropdown" data-sentry-source-file="index.tsx" />
              <Dropdown name={FormFields.Weight} label="Weight" value={values[FormFields.Weight]} options={petWeightOptions} error={formikFieldHelper(FormFields.Weight).hasError} helperText={formikFieldHelper(FormFields.Weight).errorMessage} onChange={value => setFieldValue(FormFields.Weight, value || '')} onBlur={() => {
              // https://github.com/JedWatson/react-select/issues/3737
              // Issue on mobile devices
              setTimeout(() => {
                // custom on-blur
                handleBlur({
                  target: {
                    name: FormFields.Weight
                  }
                });
              }, 0);
            }} data-sentry-element="Dropdown" data-sentry-source-file="index.tsx" />
            </Grid>
            <Grid justifyItems="center" my={['8px', '24px']} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
              <Button variant="contained" type="submit" disabled={!isValid || isSubmitting} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                Continue to chat
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>;
};